import React from 'react';
import { 
  Box, 
  Typography, 
  useTheme, 
  Paper,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface TimelineItemProps {
  year: string;
  title: string;
  company: string;
  description: string;
  isLast?: boolean;
  index: number;
}

const TimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: '1000px',
  margin: '0 auto',
  padding: theme.spacing(4, 0),
}));

const TimelineMiddleLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '50%',
  top: 0,
  bottom: 0,
  width: 2,
  backgroundColor: theme.palette.primary.main,
  transform: 'translateX(-50%)',
  [theme.breakpoints.down('md')]: {
    left: '20px',
  },
}));

const TimelineItemContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isRight' && prop !== 'isLast',
})<{ isRight: boolean; isLast: boolean }>(({ theme, isRight, isLast }) => ({
  display: 'flex',
  justifyContent: isRight ? 'flex-start' : 'flex-end',
  marginBottom: isLast ? 0 : theme.spacing(10),
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'flex-start',
    paddingLeft: theme.spacing(6),
  },
}));

const TimelineDot = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: theme.palette.background.paper,
  border: `2px solid ${theme.palette.primary.main}`,
  left: 'calc(50% - 12px)',
  top: 15,
  zIndex: 2,
  [theme.breakpoints.down('md')]: {
    left: '14px',
  },
}));

const TimelineCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '45%',
  position: 'relative',
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 10px 30px rgba(2, 12, 27, 0.7)`,
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const TimelineYear = styled(Typography)(({ theme }) => ({
  fontFamily: 'Roboto Mono, monospace',
  color: theme.palette.primary.main,
  fontWeight: 600,
}));

const TimelineItem: React.FC<TimelineItemProps> = ({ 
  year, 
  title, 
  company, 
  description, 
  isLast = false, 
  index
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isRight = index % 2 === 1;
  
  return (
    <TimelineItemContainer isRight={isRight} isLast={isLast}>
      <TimelineDot />
      <TimelineCard>
        <TimelineYear variant="subtitle1">{year}</TimelineYear>
        <Typography variant="h5" gutterBottom sx={{ color: theme.palette.text.primary }}>
          {title}
        </Typography>
        <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 500, mb: 1 }}>
          {company}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </TimelineCard>
    </TimelineItemContainer>
  );
};

interface TimelineData {
  year: string;
  title: string;
  company: string;
  description: string;
}

interface TimelineProps {
  data: TimelineData[];
}

const Timeline: React.FC<TimelineProps> = ({ data }) => {
  return (
    <TimelineContainer>
      <TimelineMiddleLine />
      {data.map((item, index) => (
        <TimelineItem
          key={index}
          year={item.year}
          title={item.title}
          company={item.company}
          description={item.description}
          isLast={index === data.length - 1}
          index={index}
        />
      ))}
    </TimelineContainer>
  );
};

export default Timeline;
